/** @jsxImportSource @emotion/react */
import Input from 'components/atoms/Input'
import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import Select from 'components/atoms/Select'
import TextArea from 'components/atoms/Textarea'
import Toast from 'components/atoms/Toast'
import {
  useCreateBillingPrepaymentMutation,
  useCreateCardSetupSessionMutation,
  useUpdateBillingPrepaymentMutation,
} from 'store/services/billingPrepayments'

type PrepaymentMethod = 'STRIPE' | 'CASH' | 'ACH' | 'CHECK' | 'CREDIT_CARD' | 'CARE_CREDIT' | 'MONEY_ORDER' | 'OTHER'

/** What was typed in when a payment was received — everything the edit form can correct. */
export type EditablePrepayment = {
  id: string
  amount: number
  method: PrepaymentMethod
  paymentStatus: 'PAID' | 'UNPAID'
  paymentDate?: string | Date | null
  checkDate?: string | Date | null
  checkNumber?: string | null
  checkAmount?: number | null
  referenceNumber?: string | null
  notes?: string | null
}

const METHOD_OPTIONS: { key: PrepaymentMethod; value: string }[] = [
  { key: 'STRIPE', value: 'Stripe' },
  { key: 'CREDIT_CARD', value: 'Credit Card' },
  { key: 'CASH', value: 'Cash' },
  { key: 'ACH', value: 'ACH' },
  { key: 'CHECK', value: 'Check' },
  { key: 'CARE_CREDIT', value: 'Care Credit' },
  { key: 'MONEY_ORDER', value: 'Money Order' },
  { key: 'OTHER', value: 'Other' },
]

const toInputDate = (value?: string | Date | null): string => {
  if (!value) return ''

  if (value instanceof Date) {
    const month = `${value.getMonth() + 1}`.padStart(2, '0')
    const day = `${value.getDate()}`.padStart(2, '0')

    return `${value.getFullYear()}-${month}-${day}`
  }

  const text = String(value)

  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : text
}

type Props = {
  open: boolean
  onClose: () => void
  patientId: number
  /**
   * The recorded payment to correct. With it the modal edits what was typed
   * in when that payment was received instead of receiving a new one.
   */
  prepayment?: EditablePrepayment
  /**
   * The order the money is being collected for, when the modal is opened from
   * an order's workflow screen. It makes the order register the payment — a
   * prepayment taken from the patient's billing page has no order and leaves
   * every order alone.
   */
  orderId?: number
  onDone?: () => void
  /** When true, a Stripe payment page opens in a new tab instead of navigating the current window. */
  stripeInNewTab?: boolean
}

export const ReceivePrepaymentModal: React.FC<Props> = ({
  open,
  onClose,
  patientId,
  prepayment,
  orderId,
  onDone,
  stripeInNewTab = false,
}) => {
  const isEdit = Boolean(prepayment)
  // Once the card has been charged, the amount and method are what Stripe
  // took, not something typed in — the rest of the details can still be
  // corrected. A checkout that is still pending is fully correctable.
  const stripeLocked = isEdit && prepayment?.method === 'STRIPE' && prepayment?.paymentStatus === 'PAID'
  const pendingStripe = isEdit && prepayment?.paymentStatus !== 'PAID'
  const getTodayDate = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = `${now.getMonth() + 1}`.padStart(2, '0')
    const day = `${now.getDate()}`.padStart(2, '0')

    return `${year}-${month}-${day}`
  }
  const [amount, setAmount] = React.useState<string>(prepayment ? String(prepayment.amount) : '')
  const [method, setMethod] = React.useState<PrepaymentMethod>(prepayment?.method || 'STRIPE')
  const [paymentDate, setPaymentDate] = React.useState<string>(toInputDate(prepayment?.paymentDate) || getTodayDate())
  const [notes, setNotes] = React.useState<string>(prepayment?.notes || '')
  const [checkDate, setCheckDate] = React.useState<string>(toInputDate(prepayment?.checkDate))
  const [checkNumber, setCheckNumber] = React.useState<string>(prepayment?.checkNumber || '')
  const [referenceNumber, setReferenceNumber] = React.useState<string>(prepayment?.referenceNumber || '')
  const [saveCardOnFile, setSaveCardOnFile] = React.useState<boolean>(false)
  const [createPrepayment, { isLoading: isCreating }] = useCreateBillingPrepaymentMutation()
  const [updatePrepayment, { isLoading: isUpdating }] = useUpdateBillingPrepaymentMutation()
  const [createCardSetupSession, { isLoading: isSetupLoading }] = useCreateCardSetupSessionMutation()
  const isLoading = isCreating || isUpdating
  // A payment received by hand can move between manual methods but not
  // become a Stripe one — there is no card charge behind it. A pending
  // Stripe checkout may still become anything.
  const methodOptions = isEdit && !stripeLocked && !pendingStripe
    ? METHOD_OPTIONS.filter((option) => option.key !== 'STRIPE')
    : METHOD_OPTIONS
  const methodLabel = METHOD_OPTIONS.find((option) => option.key === method)?.value || method
  // Saving a pending Stripe checkout under a manual method receives the money.
  const receivesOnSave = pendingStripe && method !== 'STRIPE'
  const needsPaymentDate = method !== 'STRIPE' && method !== 'CHECK'
  const needsReference = method === 'CARE_CREDIT' || method === 'MONEY_ORDER' || method === 'OTHER'
  const referenceRequired = method === 'CARE_CREDIT' || method === 'MONEY_ORDER'
  const notesRequired = method === 'OTHER'

  const openStripeUrl = (url: string) => {
    if (stripeInNewTab) {
      window.open(url, '_blank')
    } else {
      window.location.assign(url)
    }
  }

  const onSubmit = async () => {
    try {
      const num = Number(amount)

      // Patient responsibility is $0.00 but staff still need the card on file:
      // a setup-mode Stripe session saves the card without charging anything,
      // and no prepayment record is created.
      if (!isEdit && method === 'STRIPE' && saveCardOnFile && !num) {
        const result = await createCardSetupSession({ patientId, orderId }).unwrap()

        if (typeof onDone === 'function') onDone()
        onClose()

        if (result.url) openStripeUrl(result.url)
        return
      }

      if (!num || num <= 0) {
        Toast({ type: 'error', label: 'Enter valid amount' })
        return
      }

      if (needsPaymentDate && !paymentDate) {
        Toast({ type: 'error', label: 'Payment Date is required' })
        return
      }

      if (method === 'CHECK') {
        if (!checkDate) {
          Toast({ type: 'error', label: 'Check Date is required' })
          return
        }
        if (!checkNumber?.trim()) {
          Toast({ type: 'error', label: 'Check Number is required' })
          return
        }
      }

      if (referenceRequired && !referenceNumber?.trim()) {
        Toast({ type: 'error', label: 'Reference # is required' })
        return
      }

      if (notesRequired && !notes?.trim()) {
        Toast({ type: 'error', label: 'Notes are required' })
        return
      }

      if (isEdit && prepayment) {
        // The full set of details goes up; null clears whatever the chosen
        // method does not use.
        await updatePrepayment({
          patientId,
          prepaymentId: prepayment.id,
          amount: num,
          method,
          paymentDate: needsPaymentDate ? paymentDate : null,
          notes: notes?.trim() ? notes.trim() : null,
          checkDate: method === 'CHECK' ? checkDate : null,
          checkNumber: method === 'CHECK' ? checkNumber.trim() : null,
          // No separate input until batch payments: a check pays this prepayment's amount.
          checkAmount: method === 'CHECK' ? num : null,
          referenceNumber: needsReference && referenceNumber?.trim() ? referenceNumber.trim() : null,
        }).unwrap()

        if (typeof onDone === 'function') onDone()
        onClose()
        Toast({
          type: 'success',
          label: receivesOnSave ? 'Pre-payment updated and marked as paid' : 'Prepayment updated',
        })
        return
      }

      const result = await createPrepayment({
        patientId,
        amount: num,
        remaining: num,
        method,
        paymentDate: needsPaymentDate ? paymentDate : undefined,
        notes: notes?.trim() ? notes : undefined,
        checkDate: method === 'CHECK' ? checkDate : undefined,
        checkNumber: method === 'CHECK' ? checkNumber.trim() : undefined,
        checkAmount: method === 'CHECK' ? num : undefined,
        referenceNumber: needsReference && referenceNumber?.trim() ? referenceNumber.trim() : undefined,
        saveCardOnFile: method === 'STRIPE' ? saveCardOnFile : undefined,
        orderId,
        createdBy: 0,
      }).unwrap()

      if (typeof onDone === 'function') onDone()
      onClose()

      if (method === 'STRIPE' && result.stripeUrl) {
        openStripeUrl(result.stripeUrl)
        return
      }

      Toast({ type: 'success', label: 'Prepayment received' })
    } catch (e: any) {
      Toast({
        type: 'error',
        label: e?.data?.message || (isEdit ? 'Failed to update prepayment' : 'Failed to receive prepayment'),
      })
    }
  }

  return (
    <Modal
      show={open}
      onHide={onClose}
      css={{
        '.modal-content': { borderRadius: 15 },
        '.modal-body': { padding: '20px' },
      }}
      centered>
      <div css={{ textAlign: 'center', padding: '20px 0', fontWeight: 600, fontSize: 22 }}>
        {isEdit ? 'Edit Prepayment' : 'Receive Prepayment'}
      </div>
      <Modal.Body>
        <div className="row">
          <div className={needsPaymentDate ? 'col-md-4 col-12' : 'col-6'}>
            <label className="textGray">
              Amount{!isEdit && method === 'STRIPE' && saveCardOnFile ? ' (optional)' : ''}
            </label>
            <Input
              value={amount}
              name="amount"
              placeholder="$0.00"
              type="number"
              step="0.01"
              minValue={'0'}
              handleChange={(e) => setAmount(e.target.value)}
              className="form-control"
              parentDiv={null}
              disabled={stripeLocked}
            />
          </div>
          <div className={needsPaymentDate ? 'col-md-4 col-12' : 'col-6'}>
            <label className="textGray">Payment Method</label>
            <Select
              value={method}
              name="paymentMethod"
              handleChange={(e) => setMethod(e.target.value as any)}
              noDefault
              disabled={stripeLocked}
              options={methodOptions}
            />
          </div>
          {needsPaymentDate && (
            <div className="col-md-4 col-12 mt-3 mt-md-0">
              <label className="textGray">Payment Date</label>
              <Input
                value={paymentDate}
                name="paymentDate"
                type="date"
                handleChange={(e: any) => setPaymentDate(e.target.value)}
                className="form-control"
                parentDiv={null}
              />
            </div>
          )}
        </div>
        {method === 'CHECK' && (
          <>
            <div className="row mt-3">
              <div className="col-4">
                <label className="textGray">Check Date</label>
                <Input
                  value={checkDate}
                  name="checkDate"
                  type="date"
                  handleChange={(e: any) => setCheckDate(e.target.value)}
                  className="form-control"
                  parentDiv={null}
                />
              </div>
              <div className="col-4">
                <label className="textGray">Check Number</label>
                <Input
                  value={checkNumber}
                  name="checkNumber"
                  placeholder="Check #"
                  handleChange={(e) => setCheckNumber(e.target.value)}
                  className="form-control"
                  parentDiv={null}
                />
              </div>
            </div>
          </>
        )}
        {needsReference && (
          <div className="row mt-3">
            <div className="col-12">
              <label className="textGray">
                Reference #{referenceRequired ? '' : ' (optional)'}
              </label>
              <Input
                value={referenceNumber}
                name="referenceNumber"
                placeholder="Reference #"
                handleChange={(e) => setReferenceNumber(e.target.value)}
                className="form-control"
                parentDiv={null}
              />
            </div>
          </div>
        )}
        <div className="row mt-1">
          <div className="col-12">
            <TextArea
              name={'notes'}
              value={notes}
              handleChange={(e) => setNotes(e.target.value)}
              placeholder="Notes"
              label={notesRequired ? 'Notes (required)' : 'Notes'}
              rows={5}
              labelClassName="text-gray"
              disabled={!isEdit && method === 'STRIPE' && saveCardOnFile && !Number(amount)}
            />
          </div>
        </div>
        {stripeLocked && (
          <div className="row mt-1">
            <div className="col-12">
              <div
                css={{
                  fontSize: 13,
                  color: '#6c757d',
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: 6,
                  padding: '10px 12px',
                }}>
                Paid through Stripe: the amount and method are what the card was charged and cannot be changed here.
              </div>
            </div>
          </div>
        )}
        {receivesOnSave && (
          <div className="row mt-1">
            <div className="col-12">
              <div
                css={{
                  fontSize: 13,
                  color: '#856404',
                  background: '#fff3cd',
                  border: '1px solid #ffeeba',
                  borderRadius: 6,
                  padding: '10px 12px',
                }}>
                This pre-payment has not been paid through Stripe yet. Saving it as {methodLabel} marks it as paid
                right away; the money stays in Unapplied Payments until it is applied to a charge.
              </div>
            </div>
          </div>
        )}
        {!isEdit && method === 'STRIPE' && (
          <>
            <div className="row mt-1">
              <div className="col-12">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="saveCardOnFile"
                    checked={saveCardOnFile}
                    onChange={(e) => setSaveCardOnFile(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="saveCardOnFile">
                    Save card on file
                  </label>
                </div>
              </div>
            </div>
            <div className="row mt-1">
              <div className="col-12">
                <div
                  css={{
                    fontSize: 13,
                    color: '#856404',
                    background: '#fff3cd',
                    border: '1px solid #ffeeba',
                    borderRadius: 6,
                    padding: '10px 12px',
                  }}>
                  {saveCardOnFile && !Number(amount)
                    ? 'No amount entered: the card will be saved on file without charging the patient.'
                    : 'Payment page will be opened right after submission to complete the payment.'}
                </div>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end">
        <Button variant="outline-secondary" onClick={onClose} disabled={isLoading || isSetupLoading}>
          Cancel
        </Button>
        <Button className="ml-2" variant="primary" onClick={onSubmit} disabled={isLoading || isSetupLoading}>
          {isEdit ? 'Save' : 'Submit'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
